import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Fab,
  Tabs,
  Tab,
  Chip,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import LoadingView from '../components/LoadingView';
import TurquoiseLinkCustomerDialog, { TurquoiseLinkTarget } from '../components/TurquoiseLinkCustomerDialog';
import TurquoiseFilterPanel from '../components/TurquoiseFilterPanel';
import {
  STANDARD_FAB_CTA_SX,
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import {
  useTurquoiseMatchPreview,
  MatchTier,
  TabKey,
  MatchRow,
  MatchSearchFilters,
  EMPTY_FILTERS,
  hasActiveFilters,
} from '../hooks/useTurquoiseMatchPreview';
import { useAuth } from '../firebase/AuthProvider';

const TAB_LABELS: Record<TabKey, string> = {
  exact: '完全一致',
  high: '高確度',
  mid: '中確度',
  unmatched: '未マッチ',
  committed: '確定済み',
  skipped: '見送り',
};

const TAB_COLORS: Record<TabKey, 'success' | 'primary' | 'warning' | 'default' | 'info' | 'error'> = {
  exact: 'success',
  high: 'primary',
  mid: 'warning',
  unmatched: 'default',
  committed: 'info',
  skipped: 'error',
};

const TIER_TABS: TabKey[] = ['unmatched', 'exact', 'high', 'mid', 'committed', 'skipped'];

const TORIHIKISAKI_KBN_MAP: Record<string, string> = {
  '1': '得意先',
  '2': '仕入先',
  '3': '両方先',
};

/**
 * フリーワード検索欄 + 詳細検索 (取引先一覧の検索欄と同じ作り)。
 * 入力中の文字列はこのコンポーネント内に閉じ込め、Enter / 虫めがね / クリア /
 * 詳細検索の実行時だけ親へ通知する (1 文字ごとに大きなテーブルが再描画されるのを防ぐ)。
 * 詳細検索の実行時は入力欄の現在値もあわせて渡す
 * (入力を消しただけでは適用済みのワードが残り、AND 条件になってしまうため)。
 */
const MatchSearchField = React.memo(function MatchSearchField({
  keyword,
  filters,
  onSearch,
  onApplyFilters,
  disabled,
}: {
  keyword: string;
  filters: MatchSearchFilters;
  onSearch: (value: string) => void;
  onApplyFilters: (next: MatchSearchFilters, word: string) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(keyword);

  // プレビュー再実行・絞り込みチップの削除で親側の keyword がクリアされたら
  // 入力欄も追従させる (取引先一覧には無い、この画面固有の事情)
  useEffect(() => {
    setDraft(keyword);
  }, [keyword]);

  const search = () => onSearch(draft.trim());
  const clear = () => {
    setDraft('');
    onSearch('');
  };

  return (
    <TextField
      size="small"
      value={draft}
      placeholder="取引先名・カナ・コード・TEL・住所などで検索"
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          search();
        }
      }}
      disabled={disabled}
      sx={{ width: 360 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Tooltip title="検索 (Enter)">
              <IconButton size="small" onClick={search} aria-label="検索" sx={{ p: 0.5 }}>
                <Icons.Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Stack direction="row" spacing={0.25} alignItems="center">
              {draft && (
                <Tooltip title="入力をクリア">
                  <IconButton size="small" onClick={clear} aria-label="検索条件をクリア" sx={{ p: 0.5 }}>
                    <Icons.Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />
              <TurquoiseFilterPanel
                filters={filters}
                onApply={(next) => onApplyFilters(next, draft.trim())}
                disabled={disabled}
                variant="icon"
              />
            </Stack>
          </InputAdornment>
        ),
      }}
    />
  );
});


export default function TurquoiseMatchingReview() {
  const { user } = useAuth();
  const {
    summary,
    hasData,
    tab,
    page,
    pageSize,
    keyword,
    filters,
    rows,
    totalCount,
    tierSummary,
    loadingPreview,
    loadingList,
    committing,
    error,
    setTab,
    setPage,
    setPageSize,
    setKeyword,
    setFilters,
    runPreview,
    fetchList,
    patchSelected,
    patchSkipped,
    patchBulkSelected,
    commitRun,
    reset,
  } = useTurquoiseMatchPreview();

  // 詳細検索からの実行。入力欄の現在値を同時に適用し、消したはずのワードが
  // AND 条件で残るのを防ぐ
  const handleApplyFilters = useCallback(
    (next: MatchSearchFilters, word: string) => {
      setFilters(next);
      setKeyword(word);
    },
    [setFilters, setKeyword],
  );

  const isHistoryTab = tab === 'committed' || tab === 'skipped';
  // フリーワード・詳細条件のいずれかで絞り込み中か (一括操作の抑止と件数チップの表示に使う)
  const isFiltered = !!keyword || hasActiveFilters(filters);

  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [commitResult, setCommitResult] = useState<string | null>(null);
  const [skipTargetMatchId, setSkipTargetMatchId] = useState<number | null>(null);
  // 候補なし行を既存顧客へ手動で紐付けるダイアログの対象
  const [linkTarget, setLinkTarget] = useState<TurquoiseLinkTarget | null>(null);

  useEffect(() => {
    if (hasData) {
      fetchList();
    }
  }, [hasData, tab, page, pageSize, keyword, fetchList]);

  const handlePreview = useCallback(async () => {
    setCommitResult(null);
    await runPreview(85);
  }, [runPreview]);

  const handleConfirmSkip = useCallback(async () => {
    if (skipTargetMatchId === null) return;
    const targetId = skipTargetMatchId;
    setSkipTargetMatchId(null);
    await patchSkipped(targetId, user?.email || undefined);
  }, [skipTargetMatchId, patchSkipped, user]);

  const handleCommit = useCallback(async () => {
    setCommitDialogOpen(false);
    const counts = await commitRun(user?.email || undefined);
    if (counts) {
      setCommitResult(
        `確定完了 - 既存紐付け ${counts.corporate_updated} 件 / 新規登録 ${counts.corporate_inserted} 件 (法人+個人合算) / committed ${counts.committed} 件 / rejected ${counts.rejected} 件 / 重複スキップ ${counts.skipped_duplicate} 件 / 名称ブランクスキップ ${counts.skipped_blank_name} 件`
      );
      reset();
    }
  }, [commitRun, user, reset]);

  const columns: Column[] = useMemo(() => {
    return [
      {
        field: 'selected',
        headerName: isHistoryTab ? 'Tier' : '取込',
        width: isHistoryTab ? 80 : 100,
        align: 'center',
        renderCell: (row: MatchRow) =>
          isHistoryTab ? (
            <Chip
              label={TAB_LABELS[row.tier]}
              size="small"
              color={TAB_COLORS[row.tier]}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
            />
          ) : (
            <Stack direction="row" spacing={0} alignItems="center" justifyContent="center">
              <Checkbox
                checked={row.selected}
                onChange={(e) => patchSelected(row.match_id, e.target.checked)}
                size="small"
                sx={{ p: 0.5 }}
              />
              <Tooltip title="見送り (次回 preview の判定対象から除外)">
                <IconButton
                  size="small"
                  onClick={() => setSkipTargetMatchId(row.match_id)}
                  sx={{ p: 0.5, color: 'text.secondary' }}
                >
                  <Icons.Block fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
      },
      {
        field: 'torihikisaki',
        headerName: 'ターコイズ側',
        width: 360,
        renderCell: (row: MatchRow) => (
          <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}
              >
                {String(row.torihikisaki_cd ?? '').padStart(8, '0')}
              </Typography>
              <Chip
                label={row.customer_type === 'individual' ? '個人' : '法人'}
                size="small"
                color={row.customer_type === 'individual' ? 'success' : 'info'}
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
              {row.torihikisaki_kbn && TORIHIKISAKI_KBN_MAP[row.torihikisaki_kbn] && (
                <Chip
                  label={TORIHIKISAKI_KBN_MAP[row.torihikisaki_kbn]}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
              {row.torihikisaki_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {row.torihikisaki_kname}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
              TEL {row.torihikisaki_tel || '-'} / 携帯 {row.torihikisaki_keitai_tel || '-'} / FAX {row.torihikisaki_fax || '-'}
              {row.torihikisaki_email ? ` / ${row.torihikisaki_email}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
              {row.torihikisaki_post} / {row.torihikisaki_address}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'company',
        headerName: 'Cloud SQL 側候補',
        width: 360,
        renderCell: (row: MatchRow) => {
          if (!row.customer_id) {
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  (新規登録候補)
                </Typography>
                {!isHistoryTab && (
                  <Button
                    size="small"
                    startIcon={<Icons.Link sx={{ fontSize: 16 }} />}
                    onClick={() =>
                      setLinkTarget({
                        match_id: row.match_id,
                        torihikisaki_cd: row.torihikisaki_cd,
                        torihikisaki_name: row.torihikisaki_name || '',
                        customer_type: row.customer_type,
                      })
                    }
                    sx={{ ...STANDARD_TEXT_BUTTON_SX, minWidth: 0, px: 1, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                  >
                    既存顧客へ紐付け
                  </Button>
                )}
              </Stack>
            );
          }
          const fullAddress = [row.candidate_address_main, row.candidate_address_second]
            .filter(Boolean)
            .join(' ')
            .trim();
          return (
            <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                {row.company_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {row.company_name_kana}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                TEL {row.candidate_tel || '-'}
                {row.candidate_fax ? ` / FAX ${row.candidate_fax}` : ''}
                {row.candidate_email ? ` / ${row.candidate_email}` : ''}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                {row.candidate_post || '-'} / {fullAddress || '-'}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'match_reason',
        headerName: '判定根拠',
        width: 140,
        renderCell: (row: MatchRow) =>
          row.match_reason ? (
            <Chip label={row.match_reason} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          ) : null,
      },
      {
        field: 'similarity',
        headerName: '類似度',
        width: 130,
        renderCell: (row: MatchRow) => (
          <Box>
            {row.similarity_name !== null && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                名: {Number(row.similarity_name).toFixed(3)}
              </Typography>
            )}
            {row.similarity_kana !== null && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                カナ: {Number(row.similarity_kana).toFixed(3)}
              </Typography>
            )}
          </Box>
        ),
      },
    ];
  }, [patchSelected, isHistoryTab]);

  const tabCount = (t: TabKey) => {
    if (t === 'committed' || t === 'skipped') {
      // 全 Tier 横断で同 status の件数を合計
      return (['exact', 'high', 'mid', 'unmatched'] as MatchTier[]).reduce(
        (sum, x) => sum + (tierSummary[x]?.[t] || 0),
        0
      );
    }
    return tierSummary[t]?.pending || 0;
  };

  if (loadingPreview) {
    return <LoadingView message="ターコイズ全件取得と名寄せ判定を実行中" height="80vh" />;
  }

  if (committing) {
    return <LoadingView message="確定実行中 - mst_customers_corporate に反映しています" height="80vh" />;
  }

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}
        >
          <Icons.AccountTree /> ターコイズ取込レビュー
        </Typography>

        <Stack direction="row" spacing={1.5}>
          <Fab variant="extended" onClick={handlePreview} sx={STANDARD_FAB_CTA_SX} disabled={loadingPreview || committing}>
            <Icons.Refresh />
            プレビュー実行
          </Fab>
          {hasData && (
            <Fab
              variant="extended"
              onClick={() => setCommitDialogOpen(true)}
              sx={STANDARD_FAB_CTA_SX}
              disabled={committing || loadingList}
            >
              <Icons.Save />
              確定実行
            </Fab>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5, mx: 2 }}>
          {error}
        </Alert>
      )}

      {commitResult && (
        <Alert severity="success" sx={{ mt: 1.5, mx: 2 }} onClose={() => setCommitResult(null)}>
          {commitResult}
        </Alert>
      )}

      {!hasData && !loadingPreview && (
        <Card sx={{ mt: 3, mx: 2 }}>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              「プレビュー実行」を押すと、ターコイズ取引先全件を取得し、法人区分でフィルタした上で
              <strong> mst_customers_corporate </strong>
              と名寄せ判定を行います。結果は<strong>確定するまで本番反映されません</strong>。
            </Typography>
          </CardContent>
        </Card>
      )}

      {summary && (
        <Card sx={{ mt: 2, mx: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
              <Typography variant="body2">
                ターコイズ全 {summary.total_turquoise} 件 / 法人 {summary.hojin_kbn_counts['1'] || 0} 件 /
                既確定 {summary.confirmed_skipped} 件 / 見送り除外 {summary.skipped_excluded ?? 0} 件 /
                判定対象 {summary.target_count} 件
              </Typography>
              <Typography variant="body2">
                Cloud SQL 候補 {summary.corporate_candidate_count} 件 / 所要 {summary.elapsed_seconds} 秒
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {hasData && (
        <Box sx={{ mt: 2, mx: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v as TabKey)} variant="fullWidth">
            {TIER_TABS.map((t) => (
              <Tab
                key={t}
                value={t}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{TAB_LABELS[t]}</Typography>
                    <Chip
                      label={tabCount(t)}
                      color={TAB_COLORS[t]}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Divider />

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* 検索に関する操作は入力欄へ集約する (取引先一覧と同じ作り) */}
              <MatchSearchField
                keyword={keyword}
                filters={filters}
                onSearch={setKeyword}
                onApplyFilters={handleApplyFilters}
                disabled={loadingList || committing}
              />
              {isFiltered && (
                <Chip
                  label={`絞り込み ${totalCount} 件`}
                  size="small"
                  variant="outlined"
                  onDelete={() => {
                    setKeyword('');
                    setFilters(EMPTY_FILTERS);
                  }}
                  sx={{ height: 24, fontSize: '0.72rem' }}
                />
              )}
            </Stack>

            <Tooltip
              title={isFiltered ? '検索で絞り込み中は一括操作を行えません (タブ全体が対象になるため)' : ''}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ visibility: isHistoryTab ? 'hidden' : 'visible' }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icons.CheckBox />}
                  onClick={() => patchBulkSelected(true, tab as MatchTier)}
                  disabled={loadingList || committing || isHistoryTab || isFiltered}
                >
                  このタブを全選択
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icons.CheckBoxOutlineBlank />}
                  onClick={() => patchBulkSelected(false, tab as MatchTier)}
                  disabled={loadingList || committing || isHistoryTab || isFiltered}
                >
                  このタブを全解除
                </Button>
              </Stack>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {hasData && (
        <Box sx={{ ...TABLE_FILL_SX, mt: 1, mx: 2 }}>
          <StandardDataTable
            rows={rows}
            columns={columns}
            loading={loadingList}
            loadingMessage="プレビュー結果を取得中"
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSortChange={() => { }}
          />
        </Box>
      )}

      <Dialog open={skipTargetMatchId !== null} onClose={() => setSkipTargetMatchId(null)}>
        <DialogTitle>見送り確定の確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            この取引先を「見送り」にすると、Cloud SQL 側へ登録されないことに加え、
            <strong>次回以降のプレビュー実行でも自動的に判定対象から除外</strong>されます。
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            一度見送りに設定した後は UI から取り消しできません。誤操作にご注意ください。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipTargetMatchId(null)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSkip}
            startIcon={<Icons.Block />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            見送りにする
          </Button>
        </DialogActions>
      </Dialog>

      <TurquoiseLinkCustomerDialog
        open={linkTarget !== null}
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={() => fetchList()}
      />

      <Dialog open={commitDialogOpen} onClose={() => setCommitDialogOpen(false)}>
        <DialogTitle>確定実行の確認</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            現在の selected = TRUE の行を mst_customers_corporate に反映します。
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            一度確定すると元には戻せません。チェックボックスを外したい行があれば、ダイアログを閉じてから先に外してください。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommitDialogOpen(false)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={handleCommit} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={committing}>
            確定する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
